import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Stack,
  LinearProgress,
  Alert,
  Snackbar,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import {
  Search,
  Visibility,
  Reply,
  Delete,
  Download,
  Refresh,
  Email,
  Schedule,
  Person,
  ExpandMore,
  MarkEmailRead,
  MarkEmailUnread,
  Close,
  CheckCircle,
  PendingActions,
} from '@mui/icons-material';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy 
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { motion } from 'framer-motion';

const ContactManagement = () => {
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const messagesPerPage = 10;

  useEffect(() => {
    fetchMessages();
  }, []);

  useEffect(() => {
    // Filter messages based on search term and status
    let filtered = messages.filter(message => 
      message.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.message?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (statusFilter !== 'all') {
      filtered = filtered.filter(message => 
        (message.status || 'new') === statusFilter
      );
    }

    setFilteredMessages(filtered);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, messages]);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const messagesQuery = query(collection(db, 'contactMessages'), orderBy('timestamp', 'desc'));
      const snapshot = await getDocs(messagesQuery);
      const messagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate?.() || new Date(doc.data().timestamp)
      }));
      setMessages(messagesData);
    } catch (error) {
      console.error('Error fetching messages:', error);
      showSnackbar('Error fetching messages', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleViewMessage = (message) => {
    setSelectedMessage(message);
    setDialogOpen(true);
    
    // Mark as read if it's new
    if (message.status === 'new' || !message.status) {
      updateMessageStatus(message.id, 'read');
    }
  };

  const updateMessageStatus = async (messageId, status) => {
    try {
      const messageRef = doc(db, 'contactMessages', messageId);
      await updateDoc(messageRef, { 
        status,
        updatedAt: new Date()
      });
      
      // Update local state
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, status } : msg
      ));
      
      showSnackbar(`Message marked as ${status}`);
    } catch (error) {
      console.error('Error updating message status:', error);
      showSnackbar('Error updating message status', 'error');
    }
  };

  const handleDeleteMessage = async () => {
    try {
      await deleteDoc(doc(db, 'contactMessages', messageToDelete.id));
      setMessages(prev => prev.filter(msg => msg.id !== messageToDelete.id));
      setDeleteDialogOpen(false);
      setMessageToDelete(null);
      showSnackbar('Message deleted successfully');
    } catch (error) {
      console.error('Error deleting message:', error);
      showSnackbar('Error deleting message', 'error');
    }
  };

  const exportMessages = () => {
    const csvContent = [
      ['Name', 'Email', 'Subject', 'Message', 'Status', 'Date', 'IP Address'].join(','),
      ...filteredMessages.map(msg => [
        `"${msg.name || ''}"`,
        `"${msg.email || ''}"`,
        `"${msg.subject || ''}"`,
        `"${msg.message?.replace(/"/g, '""') || ''}"`,
        `"${msg.status || 'new'}"`,
        `"${msg.timestamp?.toLocaleDateString() || ''}"`,
        `"${msg.ipAddress || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fxwars-contact-messages-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'error';
      case 'read': return 'warning';
      case 'responded': return 'success';
      case 'resolved': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new': return <MarkEmailUnread />;
      case 'read': return <MarkEmailRead />;
      case 'responded': return <Reply />;
      case 'resolved': return <CheckCircle />;
      default: return <PendingActions />;
    }
  };

  // Pagination
  const indexOfLastMessage = currentPage * messagesPerPage;
  const indexOfFirstMessage = indexOfLastMessage - messagesPerPage;
  const currentMessages = filteredMessages.slice(indexOfFirstMessage, indexOfLastMessage);
  const totalPages = Math.ceil(filteredMessages.length / messagesPerPage);

  // Stats
  const newMessages = messages.filter(msg => (msg.status || 'new') === 'new').length;
  const readMessages = messages.filter(msg => msg.status === 'read').length;
  const respondedMessages = messages.filter(msg => msg.status === 'responded').length;
  const resolvedMessages = messages.filter(msg => msg.status === 'resolved').length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Contact Messages
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            startIcon={<Download />}
            variant="outlined"
            onClick={exportMessages}
            disabled={filteredMessages.length === 0}
          >
            Export CSV
          </Button>
          <Button
            startIcon={<Refresh />}
            variant="outlined"
            onClick={fetchMessages}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="overline">New Messages</Typography>
              <Typography variant="h4" fontWeight="bold" color="error.main">
                {newMessages}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="overline">Read Messages</Typography>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {readMessages}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="overline">Responded</Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {respondedMessages}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="overline">Resolved</Typography>
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {resolvedMessages}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search messages by name, email, subject, or content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Messages</MenuItem>
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="read">Read</MenuItem>
                <MenuItem value="responded">Responded</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Messages Table */}
      <Paper>
        {loading ? (
          <LinearProgress />
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Sender</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentMessages.map((message, index) => (
                    <motion.tr
                      key={message.id}
                      component={TableRow}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      sx={{ 
                        backgroundColor: (message.status || 'new') === 'new' ? 'action.hover' : 'inherit'
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {message.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {message.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 300 }}>
                          {message.subject}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          icon={getStatusIcon(message.status || 'new')}
                          label={message.status || 'new'} 
                          size="small" 
                          color={getStatusColor(message.status || 'new')}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {message.timestamp?.toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {message.timestamp?.toLocaleTimeString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="View Message">
                            <IconButton 
                              size="small" 
                              onClick={() => handleViewMessage(message)}
                              color="primary"
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          
                          {(message.status || 'new') === 'new' && (
                            <Tooltip title="Mark as Read">
                              <IconButton 
                                size="small" 
                                onClick={() => updateMessageStatus(message.id, 'read')}
                                color="warning"
                              >
                                <MarkEmailRead />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {message.status === 'read' && (
                            <Tooltip title="Mark as Responded">
                              <IconButton 
                                size="small" 
                                onClick={() => updateMessageStatus(message.id, 'responded')}
                                color="success"
                              >
                                <Reply />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {message.status === 'responded' && (
                            <Tooltip title="Mark as Resolved">
                              <IconButton 
                                size="small" 
                                onClick={() => updateMessageStatus(message.id, 'resolved')}
                                color="info"
                              >
                                <CheckCircle />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          <Tooltip title="Delete Message">
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                setMessageToDelete(message);
                                setDeleteDialogOpen(true);
                              }}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </motion.tr>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(e, page) => setCurrentPage(page)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* View Message Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Contact Message Details
          <IconButton onClick={() => setDialogOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedMessage && (
            <Stack spacing={3}>
              {/* Message Header */}
              <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="overline" color="textSecondary">From</Typography>
                    <Typography variant="h6">{selectedMessage.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {selectedMessage.email}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="overline" color="textSecondary">Date</Typography>
                    <Typography variant="body1">
                      {selectedMessage.timestamp?.toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {selectedMessage.timestamp?.toLocaleTimeString()}
                    </Typography>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="overline" color="textSecondary">Subject</Typography>
                  <Typography variant="h6">{selectedMessage.subject}</Typography>
                </Box>
              </Box>

              {/* Message Content */}
              <Box>
                <Typography variant="overline" color="textSecondary">Message</Typography>
                <Paper sx={{ p: 2, mt: 1, backgroundColor: 'grey.50' }}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedMessage.message}
                  </Typography>
                </Paper>
              </Box>

              {/* Technical Details */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">Technical Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">IP Address</Typography>
                      <Typography variant="body2">{selectedMessage.ipAddress || 'Unknown'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">User Agent</Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {selectedMessage.userAgent || 'Unknown'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Message ID</Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {selectedMessage.id}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Status Management */}
              <Box sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Update Status</Typography>
                <Stack direction="row" spacing={1}>
                  <Button 
                    size="small" 
                    variant={selectedMessage.status === 'read' ? 'contained' : 'outlined'}
                    onClick={() => updateMessageStatus(selectedMessage.id, 'read')}
                  >
                    Mark as Read
                  </Button>
                  <Button 
                    size="small" 
                    variant={selectedMessage.status === 'responded' ? 'contained' : 'outlined'}
                    onClick={() => updateMessageStatus(selectedMessage.id, 'responded')}
                  >
                    Mark as Responded
                  </Button>
                  <Button 
                    size="small" 
                    variant={selectedMessage.status === 'resolved' ? 'contained' : 'outlined'}
                    onClick={() => updateMessageStatus(selectedMessage.id, 'resolved')}
                  >
                    Mark as Resolved
                  </Button>
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this message from "{messageToDelete?.name}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteMessage} color="error" variant="contained">
            Delete Message
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactManagement; 